import {
  VStack,
  HStack,
  Box,
  Text,
  Icon,
  Badge,
  useColorModeValue,
} from '@chakra-ui/react'
import { 
  CheckCircleIcon, 
  WarningIcon,
  StarIcon,
  RepeatIcon,
  ChevronRightIcon,
  ViewIcon,
} from '@chakra-ui/icons'
import { PRM_CombinedResponse } from '../types/unified_models'

interface ResultsOverviewProps {
  response: PRM_CombinedResponse | null
  onNavigateToTab: (tabIndex: number) => void
}

export default function ResultsOverview({ response, onNavigateToTab }: ResultsOverviewProps) {
  // const bgColor = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const textColor = useColorModeValue('gray.800', 'gray.100')
  const mutedColor = useColorModeValue('gray.600', 'gray.400')

  if (!response) {
    return (
      <VStack align="stretch" spacing={4} p={4}>
        <Text fontSize="lg" fontWeight="bold" color={textColor}>
          Results Overview
        </Text>
        <Text color={mutedColor}>
          Submit your solution to see results here.
        </Text>
      </VStack>
    )
  }

  // Helper function to filter benchmark results based on what submission actually ran
  // This uses the same logic as BenchmarkModule to ensure consistency
  const filterBenchmarkResultsByActualResults = (
    referenceResults: any[] | any,
    challengeResults: any[] | any,
    submissionResults: any[] | any
  ) => {
    // Convert to arrays for consistent processing
    const refArray: any[] = Array.isArray(referenceResults) 
      ? referenceResults 
      : referenceResults ? [referenceResults] : []
    const subArray: any[] = Array.isArray(submissionResults) 
      ? submissionResults 
      : submissionResults ? [submissionResults] : []
    const challArray: any[] = Array.isArray(challengeResults) 
      ? challengeResults 
      : challengeResults ? [challengeResults] : []

    // Use the same matching logic as BenchmarkModule
    const matches = (name?: string, target?: string) => {
      if (!name || !target) return false
      const a = String(name).toLowerCase()
      const b = String(target).toLowerCase()
      return a === b || a.includes(b) || b.includes(a)
    }

    // Determine if we should filter based on actual results structure
    // If we have exactly one submission result but multiple reference results,
    // we should filter to match only the submission's input
    const shouldFilter = subArray.length === 1 && refArray.length > 1
    
    if (!shouldFilter) {
      return { referenceResults, challengeResults, submissionResults }
    }

    // Filter based on the single submission result's benchmark_name
    const targetBenchmarkName = subArray[0]?.benchmark_name
    if (!targetBenchmarkName) {
      return { referenceResults, challengeResults, submissionResults }
    }

    const filteredRefs = refArray.filter(r => matches(r.benchmark_name, targetBenchmarkName))
    const filteredChalls = challArray.filter(c => matches(c.benchmark_name, targetBenchmarkName))

    return {
      referenceResults: filteredRefs,
      challengeResults: filteredChalls,
      submissionResults: subArray
    }
  }

  // Calculate overall status
  const hasCorrectness = response.correctness && !('error' in response.correctness)
  const hasBenchmark = response.benchmark && !('error' in response.benchmark)
  const hasProfiler = response.profiler && !('error' in response.profiler)
  const hasCilksan = response.cilksan && !('error' in response.cilksan)
  const hasCilkscale = response.cilkscale_benchmark && !('error' in response.cilkscale_benchmark)

  const correctnessPassed = hasCorrectness && 
    (response.correctness as any)?.cases_failed === 0 && 
    (response.correctness as any)?.checks_failed === 0

  const allTestsPassed = correctnessPassed && 
    (!response.benchmark || !('error' in response.benchmark))

  // Calculate performance metrics with robust matching/filtering
  let speedup = 0
  if (hasBenchmark && response.benchmark && !('error' in response.benchmark)) {
    const benchmark = response.benchmark as any
    
    // Always normalize and filter to ensure we match on benchmark_name
    const { referenceResults, submissionResults } = filterBenchmarkResultsByActualResults(
      benchmark.reference,
      benchmark.challenges || [],
      benchmark.submission
    )

    const refArray: any[] = Array.isArray(referenceResults)
      ? (referenceResults as any[])
      : referenceResults ? [referenceResults] : []
    const subArray: any[] = Array.isArray(submissionResults)
      ? (submissionResults as any[])
      : submissionResults ? [submissionResults] : []

    if (subArray.length > 0 && refArray.length > 0) {
      const speedups: number[] = []
      subArray.forEach(sub => {
        const ref = refArray.find(r => {
          const a = String(r?.benchmark_name ?? '').toLowerCase()
          const b = String(sub?.benchmark_name ?? '').toLowerCase()
          return a === b || a.includes(b) || b.includes(a)
        }) || refArray[0]
        if (ref && ref.runtime > 0 && sub.runtime > 0) {
          speedups.push(ref.runtime / sub.runtime)
        }
      })
      if (speedups.length > 0) {
        // Use geometric mean for consistency across one or many inputs
        speedup = Math.pow(speedups.reduce((a, b) => a * b, 1), 1 / speedups.length)
      }
    }
  }

  // Count races
  const raceCount = hasCilksan ? (response.cilksan as any)?.distinct_races || 0 : null

  return (
    <VStack align="stretch" spacing={4} p={4} w="100%" overflow="hidden">
      {/* Overall Status */}
      <Box
        p={4}
        bg={allTestsPassed ? "green.50" : "red.50"}
        borderRadius="md"
        borderWidth={1}
        borderColor={allTestsPassed ? "green.200" : "red.200"}
      >
        <HStack>
          <Icon 
            as={allTestsPassed ? CheckCircleIcon : WarningIcon} 
            color={allTestsPassed ? "green.500" : "red.500"} 
            boxSize={6} 
          />
          <Text fontWeight="bold" color={allTestsPassed ? "green.800" : "red.800"}>
            {allTestsPassed ? "All Tests Passed" : "Tests Failed"}
          </Text>
        </HStack>
      </Box>

      {/* Summary Cards */}
      <VStack spacing={3}>
        {/* Correctness Card */}
        <Box
          p={4}
          borderWidth={1}
          borderColor={borderColor}
          borderRadius="md"
          w="100%"
          cursor="pointer"
          transition="all 0.2s"
          _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
          onClick={() => onNavigateToTab(1)} // Navigate to Tests tab
        >
          <HStack justify="space-between">
            <VStack align="start" spacing={1}>
              <HStack>
                <Icon 
                  as={correctnessPassed ? CheckCircleIcon : WarningIcon} 
                  color={correctnessPassed ? "green.500" : "red.500"} 
                />
                <Text fontWeight="medium">Correctness</Text>
              </HStack>
              {hasCorrectness ? (
                <>
                  <Text fontSize="2xl" fontWeight="bold" color={correctnessPassed ? "green.600" : "red.600"}>
                    {(response.correctness as any)?.cases_passed}/{(response.correctness as any)?.cases_passed + (response.correctness as any)?.cases_failed}
                  </Text>
                  <Text fontSize="sm" color={mutedColor}>
                    {correctnessPassed ? "All tests passed" : `${(response.correctness as any)?.cases_failed} tests failed`}
                  </Text>
                </>
              ) : (
                <Text fontSize="sm" color="red.500">
                  Compilation failed
                </Text>
              )}
            </VStack>
            <Icon as={ChevronRightIcon} color={mutedColor} />
          </HStack>
        </Box>

        {/* Performance Card */}
        {hasBenchmark && (
          <Box
            p={4}
            borderWidth={1}
            borderColor={borderColor}
            borderRadius="md"
            w="100%"
            cursor="pointer"
            transition="all 0.2s"
            _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
            onClick={() => onNavigateToTab(2)} // Navigate to Performance tab
          >
            <HStack justify="space-between">
              <VStack align="start" spacing={1}>
                <HStack>
                  <Icon as={StarIcon} color="yellow.500" />
                  <Text fontWeight="medium">Performance</Text>
                </HStack>
                <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                  {speedup.toFixed(2)}x
                </Text>
                <Text fontSize="sm" color={mutedColor}>
                  Speedup vs reference
                </Text>
              </VStack>
              <Icon as={ChevronRightIcon} color={mutedColor} />
            </HStack>
          </Box>
        )}

        {/* Code Quality Card */}
        {(hasCilksan || hasCilkscale) && (
          <Box
            p={4}
            borderWidth={1}
            borderColor={borderColor}
            borderRadius="md"
            w="100%"
            cursor="pointer"
            transition="all 0.2s"
            _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
            onClick={() => onNavigateToTab(3)} // Navigate to Analysis tab
          >
            <HStack justify="space-between">
              <VStack align="start" spacing={1}>
                <HStack>
                  <Icon as={RepeatIcon} color="teal.500" />
                  <Text fontWeight="medium">Code Quality</Text>
                </HStack>
                <VStack align="start" spacing={2} mt={2}>
                  {hasCilksan && (
                    <HStack>
                      <Badge colorScheme={raceCount === 0 ? "green" : "red"} fontSize="xs">
                        {raceCount === 0 ? "✓" : "!"}
                      </Badge>
                      <Text fontSize="sm">
                        {raceCount === 0 ? "No races detected" : `${raceCount} races found`}
                      </Text>
                    </HStack>
                  )}
                  {hasCilkscale && (response.cilkscale_benchmark as any)?.data?.[0] && (
                    <HStack>
                      <Badge colorScheme="blue" fontSize="xs">
                        {((response.cilkscale_benchmark as any).data[0].span_speedup[0] * 100).toFixed(0)}%
                      </Badge>
                      <Text fontSize="sm">Parallelism efficiency</Text>
                    </HStack>
                  )}
                </VStack>
              </VStack>
              <Icon as={ChevronRightIcon} color={mutedColor} />
            </HStack>
          </Box>
        )}

        {/* Profiler Card */}
        {hasProfiler && (
          <Box
            p={4}
            borderWidth={1}
            borderColor={borderColor}
            borderRadius="md"
            w="100%"
            cursor="pointer"
            transition="all 0.2s"
            _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
            onClick={() => onNavigateToTab(3)} // Navigate to Analysis tab
          >
            <HStack justify="space-between">
              <VStack align="start" spacing={1}>
                <HStack>
                  <Icon as={ViewIcon} color="purple.500" />
                  <Text fontWeight="medium">Performance Profile</Text>
                </HStack>
                <Text fontSize="sm" color={mutedColor}>
                  Line-by-line analysis available
                </Text>
              </VStack>
              <Icon as={ChevronRightIcon} color={mutedColor} />
            </HStack>
          </Box>
        )}
      </VStack>


    </VStack>
  )
}
